'use strict'

import {helpers} from 'commerce-sdk-isomorphic'
import {onClient} from '@salesforce/commerce-sdk-react/utils'
import {
    SLAS_SECRET_PLACEHOLDER,
    SLAS_SECRET_WARNING_MSG
} from '@salesforce/commerce-sdk-react/constant'
import {useQueryClient, useMutation} from '@tanstack/react-query'
import {updateCache} from '@salesforce/commerce-sdk-react/hooks/utils'
import useAuthContext from '@salesforce/commerce-sdk-react/hooks/useAuthContext'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

// Helpers functions
function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }

const noop = () => ({})

function setRefreshTokenTTL(res, isGuest, rememberme, _this8) {
        const config = getConfig()
        const refreshTokenKey = isGuest ? 'refresh_token_guest' : 'refresh_token_registered' // isGuest is always false as this is being called after loginRegisteredUserB2C i.e. customer is always registered
        // refreshTokenTTL is set to default 30 days (as recieved in response) if user chose for rememberme else it is the value from config or 30 mins (1800s) 
        const refreshTokenTTLValue = rememberme ? res.refresh_token_expires_in : config.app.REFRESH_TOKEN_TTL || 1800;  
        const expiresDate = new Date(Date.now() + refreshTokenTTLValue * 1000)
        _this8.set('refresh_token_expires_in', refreshTokenTTLValue.toString())
        _this8.set(refreshTokenKey, res.refresh_token, {
            expires: expiresDate
        })
        _this8.set('usid', res.usid, {
            expires: expiresDate
        })
    }

/**
 * Custom React hook that provides an auxiliary authentication helper for handling the
 * 'loginRegisteredUserB2C' mutation using the authentication context and React Query's mutation.
 *
 * Throws an error if an unknown mutation is provided or if the mutation is not 'loginRegisteredUserB2C'.
 * Handles login logic, manages client secrets, updates cache on successful authentication,
 * and ensures proper session handling on the client side.
 *
 * @param {string} mutation - The name of the authentication mutation to use (must be 'loginRegisteredUserB2C').
 * @returns {import('@tanstack/react-query').UseMutationResult} - The mutation result object from React Query.
 * @throws {Error} If an unknown or unsupported mutation is provided.
 */
function useAuthAuxHelper(mutation) {
    const auth = useAuthContext()

    if (!auth[mutation]) throw new Error(`Unknown login helper mutation: ${mutation}`)
    if (mutation !== 'loginRegisteredUserB2C') {
        throw new Error('Use useAuthHelper from @salesforce/commerce-sdk-react')
    }

    const queryClient = useQueryClient()

    const method = function (args) {
        var _this8 = this
        var credentials = args.credentials
        var body = args.body

        return _asyncToGenerator(function* () {
            if (_this8.clientSecret && onClient() && _this8.clientSecret !== SLAS_SECRET_PLACEHOLDER) {
                _this8.logWarning(SLAS_SECRET_WARNING_MSG)
            }
            const redirectURI = _this8.redirectURI
            const usid = _this8.get('usid')
            const dntPref = _this8.getDnt({
                includeDefaults: true
            });
            const isGuest = false
            const rememberme = body.rememberme || false
            const token = yield helpers.loginRegisteredUserB2C(_this8.client, _objectSpread(_objectSpread({}, credentials), {}, {
                clientSecret: _this8.clientSecret
            }), _objectSpread({
                redirectURI,
                dnt: dntPref
            }, usid && {
                usid
            }),
            {
                body
            })

            _this8.handleTokenResponse(token, isGuest)
            setRefreshTokenTTL(token, isGuest, rememberme, _this8)
            if (onClient()) {
                void _this8.clearECOMSession()
            }
            return token
        })()
    }.bind(auth)

    return useMutation(auth.whenReady(method), {
        onSuccess(data, options) {
            const getCacheUpdates = cacheUpdateMatrix[mutation]
            if (getCacheUpdates) {
                const cacheUpdates = getCacheUpdates(options, data)
                updateCache(queryClient, cacheUpdates, data)
            }
        }
    })
}

const cacheUpdateMatrix = {
    loginRegisteredUserB2C: noop
}

export {useAuthAuxHelper}
